// components/InvoiceSaveLoad.js
import React, { useState, useEffect } from 'react';
import { Save, FolderOpen, Trash2, Plus, FileText, Calendar, X, Check } from 'lucide-react';

const InvoiceSaveLoad = ({ 
  invoiceData, 
  onLoadInvoice, 
  onClearForm, 
  isVisible, 
  onClose 
}) => {
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // 저장된 견적서 목록 불러오기
  useEffect(() => {
    loadSavedInvoices();
  }, []);

  const loadSavedInvoices = () => {
    const saved = localStorage.getItem('savedInvoices');
    if (saved) {
      setSavedInvoices(JSON.parse(saved));
    }
  };

  // 견적서 저장
  const handleSaveInvoice = () => {
    if (!saveName.trim()) {
      alert('견적서 이름을 입력해주세요.');
      return;
    }

    setSaving(true);

    const newInvoice = {
      id: Date.now(),
      name: saveName.trim(),
      createdAt: new Date().toISOString(),
      data: { ...invoiceData }
    };

    const updatedInvoices = [...savedInvoices, newInvoice];
    localStorage.setItem('savedInvoices', JSON.stringify(updatedInvoices));
    setSavedInvoices(updatedInvoices);
    setSaveName('');
    setShowSaveForm(false);
    setSaving(false);
    alert('견적서가 저장되었습니다.');
  };

  // 견적서 불러오기
  const handleLoadInvoice = (invoice) => {
    if (window.confirm(`'${invoice.name}' 견적서를 불러오시겠습니까? 현재 작업 중인 내용은 사라집니다.`)) {
      onLoadInvoice(invoice.data);
      onClose();
      alert('견적서를 불러왔습니다.');
    }
  };

  // 견적서 삭제
  const handleDeleteInvoice = (invoiceId, invoiceName) => {
    if (window.confirm(`'${invoiceName}' 견적서를 삭제하시겠습니까?`)) {
      const updatedInvoices = savedInvoices.filter(invoice => invoice.id !== invoiceId);
      localStorage.setItem('savedInvoices', JSON.stringify(updatedInvoices));
      setSavedInvoices(updatedInvoices);
      alert('견적서가 삭제되었습니다.');
    }
  };

  // 새 견적서 시작
  const handleNewInvoice = () => {
    if (window.confirm('새 견적서를 시작하시겠습니까? 현재 작업 중인 내용은 사라집니다.')) {
      onClearForm();
      onClose();
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
        {/* 헤더 */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            견적서 관리
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* 액션 버튼들 */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={handleNewInvoice}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              새 견적서
            </button>
            <button
              onClick={() => setShowSaveForm(!showSaveForm)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
            >
              <Save className="w-4 h-4" />
              현재 견적서 저장
            </button>
          </div>

          {/* 저장 폼 */}
          {showSaveForm && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="견적서 이름을 입력하세요"
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && handleSaveInvoice()}
                />
                <button
                  onClick={handleSaveInvoice}
                  disabled={saving}
                  className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
                >
                  {saving ? (
                    <>저장 중...</>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      저장
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowSaveForm(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
                >
                  취소
                </button>
              </div>
            </div>
          )}

          {/* 저장된 견적서 목록 */}
          <div className="overflow-y-auto max-h-96">
            {savedInvoices.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">저장된 견적서가 없습니다</p>
                <p className="text-sm">첫 번째 견적서를 저장해보세요!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedInvoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-medium text-gray-900 truncate flex-1 mr-2">
                        {invoice.name}
                      </h3>
                      <button
                        onClick={() => handleDeleteInvoice(invoice.id, invoice.name)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="삭제"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="text-sm text-gray-500 mb-3 space-y-1">
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(invoice.createdAt)}
                      </div>
                      <div>고객: {invoice.data.customerInfo?.name || '미입력'}</div>
                      <div>품목 수: {invoice.data.items?.length || 0}개</div>
                    </div>
                    
                    <button
                      onClick={() => handleLoadInvoice(invoice)}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-md text-sm flex items-center justify-center gap-1 transition-colors"
                    >
                      <FolderOpen className="w-4 h-4" />
                      불러오기
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSaveLoad;